<script>
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'LandingPage',
  props: {
    previousAnswer: {
      type: String,
      default: null
    },
    /* Anchor roles (PD-1258). `canAnchor` gates the switch entirely (feature off,
       or the user isn't in levelling_anchor_roles_admin); `isAnchor` is the
       current intent, which locks the method to a full evaluation. */
    canAnchor: {
      type: Boolean,
      default: false
    },
    isAnchor: {
      type: Boolean,
      default: false
    },
  },
  emits: ['selectFlow', 'back', 'update:isAnchor']
})
</script>

<template>
  <div class="lvl-eval">
    <div class="lvl-qcard lvl-setlevel-card">
      <div class="lvl-stage-in">
        <div class="lvl-stage-head">
          <h2>How would you like to level this role?</h2>
          <p>Both methods reach a level on the same framework. Evaluation is the more thorough, point-scored route; classification is the quicker one.</p>
        </div>
        <!-- Anchor roles (PD-1258): asked BEFORE the method, because the answer
             constrains which methods are available. -->
        <label v-if="canAnchor" class="lvl-anchor-ask" :class="{ 'is-on': isAnchor }">
          <span class="lvl-anchor-ask-main">
            <span class="t"><span class="ms" style="font-size:15px">anchor</span> Is this an anchor role?</span>
            <span class="s">
              <template v-if="isAnchor">
                It becomes a calibration reference other roles are compared against — so it needs a full,
                point-scored evaluation. Classification isn’t available on this path.
              </template>
              <template v-else>
                <!-- Prototype copy: states the CONSEQUENCE before the switch is
                     thrown, so the user learns it forces a full evaluation without
                     having to turn it on and see Classify grey out. -->
                Anchors are the calibration reference other roles are compared against. They need a full
                evaluation, so choosing this selects Evaluate.
              </template>
            </span>
          </span>
          <span class="lvl-anchor-ask-sw">
            <input
              type="checkbox"
              :checked="isAnchor"
              aria-label="Is this an anchor role?"
              @change="$emit('update:isAnchor', $event.target.checked)"
            />
            <span class="kn"></span>
          </span>
        </label>

        <!-- No CEO-ceiling switch here, deliberately. In the design prototype the
             ceiling is offered ONLY from an existing role's record (LvlAnchorBar →
             LvlAnchorModal, gated `info && (info.ceo || !ceil)`), i.e. on a role that
             is ALREADY a confirmed anchor and therefore already evaluated. At the
             method step this role has neither, so there is nothing to layer a ceiling
             onto — it is set from the levelling record's anchor chip instead. -->

        <div class="lvl-choices">
          <button
            type="button"
            class="lvl-choice"
            :class="{ sel: previousAnswer === 'standard' }"
            @click="$emit('selectFlow', 'standard')"
            data-testid="evaluate-option-standard"
          >
            <span class="lvl-choice-ic lvl-choice-ic--solid"><span class="ms" style="font-size:24px">fact_check</span></span>
            <span class="lvl-choice-body">
              <span class="lvl-choice-th"><span class="lvl-choice-t">Evaluate</span><span class="lvl-choice-pill">Greater detail and accuracy</span></span>
              <span class="lvl-choice-d">Score the role against each factor and sub factor to produce a precise, defensible level. Use for anchor roles, or when you need a more rigorous, detailed audit trail.</span>
              <span class="lvl-choice-meta"><span class="ms" style="font-size:15px">schedule</span> About 10–15 min · a scored question for every sub-factor</span>
            </span>
            <span class="lvl-choice-go"><span class="ms">arrow_forward</span></span>
          </button>

          <!-- <button
            type="button"
            class="lvl-choice"
            :class="{ sel: previousAnswer === 'guided' }"
            @click="$emit('selectFlow', 'guided')"
            data-testid="evaluate-option-guided"
          >
            <span class="lvl-choice-ic"><span class="ms" style="font-size:24px">quiz</span></span>
            <span class="lvl-choice-body">
              <span class="lvl-choice-th"><span class="lvl-choice-t">Guided questionnaire</span></span>
              <span class="lvl-choice-d">Answer simple questions that automatically determine the appropriate levels.</span>
            </span>
            <span class="lvl-choice-go"><span class="ms">arrow_forward</span></span>
          </button> -->

          <button
            type="button"
            class="lvl-choice"
            :class="{ sel: previousAnswer === 'classification', 'is-locked': isAnchor }"
            :disabled="isAnchor"
            :title="isAnchor ? 'Not available — anchor roles need a full evaluation.' : undefined"
            @click="$emit('selectFlow', 'classification')"
            data-testid="evaluate-option-classification"
          >
            <span class="lvl-choice-ic"><span class="ms" style="font-size:24px">auto_awesome_motion</span></span>
            <span class="lvl-choice-body">
              <span class="lvl-choice-th"><span class="lvl-choice-t">Classify</span><span
                class="lvl-choice-pill"
                :class="{ 'lvl-choice-pill--off': isAnchor }"
              >{{ isAnchor ? 'Not available for anchors' : 'Quickest method' }}</span></span>
              <span class="lvl-choice-d">Answer one simple question per factor to get a recommended level. Best for a quick, confident placement when you have a known start point and anchor roles to calibrate against.</span>
              <span class="lvl-choice-meta"><span class="ms" style="font-size:15px">schedule</span> About 5 min · one quick question per factor</span>
            </span>
            <span class="lvl-choice-go"><span class="ms">arrow_forward</span></span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" src="./evaluationTheme.scss"></style>

<style scoped>
/* The panel centres itself via .lvl-setlevel-card (max-width + margin auto). */
.lvl-choice.sel {
  border-color: var(--v3-accent);
  box-shadow: inset 0 0 0 1px var(--v3-accent);
  background: color-mix(in srgb, var(--v3-accent) 5%, var(--v3-surface));
}
</style>
